import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useParams } from 'react-router-dom';
import { Grid, Field } from 'nallian-shared-ui/lib/components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  CheckBoxFieldValueComponent,
  SelectFieldValueComponent,
} from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import { FormModal } from 'nallian-shared-ui/lib/modules';
import organisationsService from '../../../organisations/services/organisationsService';
import communityRolesService from '../../services/communityRoles/communityRolesService';
import { generateErrorsForRequiredFields } from '../../../app/lib/validationHelper';
import { Hidden } from '../../../app/styles';

const FORM_NAME = 'communityMember';
const REQUIRED_FIELDS = [];

const validate = values => {
  let errors = {};
  errors = generateErrorsForRequiredFields(REQUIRED_FIELDS, values, errors);

  return errors;
};

const CommunityMemberForm = ({
  getOrganisationsForSelectOptions,
  hasOrganisationIdField,
  organisationsFetch,
  dispatchOrganisationsGet,
  dispatchCommunityRolesGet,
  communityRolesFetch,
  submitProcessing,
  dataLoading,
  data,
  returnRoute,
  onSubmit,
  header,
}) => {
  const { t } = useTranslation();
  const { id } = useParams();

  useEffect(dispatchOrganisationsGet, []);

  useEffect(() => dispatchCommunityRolesGet(id, t), [dispatchCommunityRolesGet, id, t]);

  const communityMembers = useSelector(state => state.repository.communityMembers?.value || []);
  const organisations = getOrganisationsForSelectOptions();

  const organisationOptions = useMemo(
    () =>
      (organisations || []).filter(
        // Excludes currently loaded community members. This is paged so not every member is excluded if all pages aren't loaded!
        item => !(communityMembers || []).find(cm => cm.organisationId === item.key)
      ),
    [organisations, communityMembers]
  );

  const roleOptions = useMemo(
    () =>
      (communityRolesFetch.value?.communityRoles || []).map(r => ({
        key: r.tag,
        value: r.tag,
        text: r.alias || r.name,
      })),
    [communityRolesFetch.value]
  );

  return (
    <FormModal
      closeIcon
      header={header}
      dataLoading={dataLoading}
      submitProcessing={submitProcessing}
      onSubmit={onSubmit}
      data={data}
      t={t}
      validate={validate}
      form={FORM_NAME}
      cancelRoute={returnRoute}
      cancelButtonContent={t('cancel')}
      saveButtonIcon="save"
      saveButtonContent={t('save')}
    >
      <Grid>
        {hasOrganisationIdField && (
          <Grid.Column mobile={8} tablet={8} computer={8}>
            <Field
              name="organisationId"
              valueComponent={SelectFieldValueComponent}
              loading={organisationsFetch.pending}
              label={t('organisation')}
              options={organisationOptions}
              disabled={submitProcessing || dataLoading}
              search
              fluid
            />
          </Grid.Column>
        )}
        <Grid.Column mobile={8} tablet={8} computer={8}>
          <Field
            name="communityRoles"
            label={t('roles')}
            valueComponent={SelectFieldValueComponent}
            options={roleOptions}
            disabled={submitProcessing || dataLoading}
            multiple
            search
            fluid
          />
        </Grid.Column>
        <Hidden>
          <Grid.Column mobile={16} tablet={16} computer={16}>
            <Field
              fluid
              label="Owner"
              name="isOwner"
              valueComponent={CheckBoxFieldValueComponent}
              disabled={submitProcessing || dataLoading}
              toggle
            />
            <Field
              fluid
              label="Hidden"
              name="isHidden"
              valueComponent={CheckBoxFieldValueComponent}
              disabled={submitProcessing || dataLoading}
              toggle
            />
          </Grid.Column>
        </Hidden>
      </Grid>
    </FormModal>
  );
};

CommunityMemberForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  dataLoading: PropTypes.bool,
  submitProcessing: PropTypes.bool,
  header: PropTypes.string.isRequired,
  returnRoute: PropTypes.string.isRequired,
  hasOrganisationIdField: PropTypes.bool,
  getOrganisationsForSelectOptions: PropTypes.func.isRequired,
  organisationsFetch: PropTypes.object.isRequired,
  dispatchOrganisationsGet: PropTypes.func.isRequired,
  dispatchCommunityRolesGet: PropTypes.func.isRequired,
  communityRolesFetch: PropTypes.object.isRequired,
  data: PropTypes.object,
};

CommunityMemberForm.defaultProps = {
  dataLoading: false,
  submitProcessing: false,
  hasOrganisationIdField: false,
  data: {},
};

export default compose(organisationsService, communityRolesService)(CommunityMemberForm);
